<script setup>
import {nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import {router, usePage} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import MessageBubble from "@/Components/CommunityChat/MessageBubble.vue";
import EmojiPickerButton from "@/Components/CommunityChat/EmojiPickerButton.vue";
import GifPickerButton from "@/Components/CommunityChat/GifPickerButton.vue";
import Input from "@/Components/Input.vue";
import MaskIcon from "@/Components/MaskIcon.vue";
import Spinner from "@/Components/Icons/Spinner.vue";
import {loadEcho} from "@/helper.js";

const props = defineProps({
    channel: String,
    fullscreen: {
        type: Boolean,
        default: false,
    },
})
const emit = defineEmits(['update:lastMessage'])

const page = usePage()
const messages = ref([])
const nextPageUrl = ref(null)
const isLoading = ref(false)
const isLoadingMore = ref(false)
const body = ref('')
const asAdmin = ref(false)
const listEl = ref(null)
let echoChannel = null
let echoListener = null

const scrollToBottom = () => {
    // A single nextTick isn't always enough here: the list can still be mid-transition
    // out of Collapse's "hidden" class (a plain DOM toggle, not part of Vue's own patch)
    // when this runs, so scrollHeight would be measured against a not-yet-visible box.
    // Waiting a frame after nextTick gives the browser one more paint to settle on.
    nextTick(() => {
        requestAnimationFrame(() => {
            if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight
        })
    })
}

const emitLastMessage = () => {
    emit('update:lastMessage', messages.value[messages.value.length - 1] ?? null)
}

const fetchMessages = () => {
    isLoading.value = true
    axios.get(route('chat.messages.index', {channel: props.channel})).then(({data}) => {
        messages.value = [...data.data].reverse()
        nextPageUrl.value = data.next_page_url
        scrollToBottom()
        emitLastMessage()
    }).finally(() => {
        isLoading.value = false
    })
}

const loadMore = () => {
    if (!nextPageUrl.value || isLoadingMore.value) return
    isLoadingMore.value = true
    const previousHeight = listEl.value?.scrollHeight ?? 0
    axios.get(nextPageUrl.value).then(({data}) => {
        messages.value = [...[...data.data].reverse(), ...messages.value]
        nextPageUrl.value = data.next_page_url
        nextTick(() => {
            if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight - previousHeight
        })
    }).finally(() => {
        isLoadingMore.value = false
    })
}

const onScroll = () => {
    if (listEl.value && listEl.value.scrollTop < 80) {
        loadMore()
    }
}

const goToLogin = () => router.get(route('login'))
const goToLoginIfGuest = () => {
    if (!page.props.auth.user) goToLogin()
}

const pushOptimistic = (fields) => {
    const tempId = 'opt_' + Date.now()
    // Mirrors what the server will actually broadcast for an admin message (no real
    // user attached) so the sender's own optimistic bubble doesn't briefly flash their
    // real name before the real, anonymized response replaces it.
    const isAdminMessage = asAdmin.value && page.props.can_post_as_chat_admin
    messages.value.push({
        id: tempId,
        type: 'text',
        body: null,
        attachment_url: null,
        user_id: isAdminMessage ? null : page.props.auth.user.id,
        user: isAdminMessage ? null : page.props.auth.user,
        is_admin_message: isAdminMessage,
        created_at: new Date().toISOString(),
        ...fields,
    })
    scrollToBottom()
    emitLastMessage()
    return tempId
}

const postMessage = (tempId, payload, rollback) => {
    axios.post(route('chat.messages.store', {channel: props.channel}), payload).then(({data}) => {
        const idx = messages.value.findIndex(m => m.id === tempId)
        if (idx !== -1) messages.value.splice(idx, 1, data)
        emitLastMessage()
    }).catch(() => {
        messages.value = messages.value.filter(m => m.id !== tempId)
        rollback?.()
        emitLastMessage()
    })
}

const send = () => {
    if (!page.props.auth.user) {
        goToLogin()
        return
    }
    const value = body.value.trim()
    if (!value) return

    body.value = ''
    const tempId = pushOptimistic({body: value})
    postMessage(tempId, {type: 'text', body: value, as_admin: asAdmin.value}, () => {
        body.value = value
    })
}

const sendGif = (gif) => {
    if (!page.props.auth.user) {
        goToLogin()
        return
    }

    const tempId = pushOptimistic({type: 'gif', attachment_url: gif.url})
    postMessage(tempId, {type: 'gif', attachment_url: gif.url, as_admin: asAdmin.value})
}

const insertEmoji = (unicode) => {
    body.value += unicode
}

const remove = (message) => {
    axios.delete(route('chat.messages.destroy', {message: message.id})).then(() => {
        messages.value = messages.value.filter(m => m.id !== message.id)
        emitLastMessage()
    })
}

const subscribe = () => {
    loadEcho(page.props.reverb, () => {
        // ChatWidget keeps its own always-on subscription to every channel (for the
        // collapsed preview), and Echo caches/shares channel instances by name - so
        // leaving the channel outright on cleanup would also kill that unrelated
        // listener. Only remove this component's own callback instead.
        echoListener = (incoming) => {
            if (!incoming?.id) return
            if (messages.value.some(m => m.id === incoming.id)) return
            if (incoming.user_id === page.props.auth.user?.id) return
            // An admin message never carries a real user_id over the broadcast (see
            // ChatMessage::toPublicArray()), so the check above can never recognize "this is
            // the message I just sent myself" for one - without this, sync broadcasting (queue
            // is sync everywhere in this app) can deliver the echo back to the sender's own tab
            // before their own POST response does, duplicating their own optimistic bubble.
            // Reconcile it with the matching still-pending optimistic entry instead of pushing
            // a second copy; postMessage()'s own tempId lookup becomes a no-op once this runs.
            if (incoming.is_admin_message) {
                const pendingIdx = messages.value.findIndex(m => String(m.id).startsWith('opt_')
                    && m.is_admin_message && m.type === incoming.type
                    && m.body === incoming.body && m.attachment_url === incoming.attachment_url)
                if (pendingIdx !== -1) {
                    messages.value.splice(pendingIdx, 1, incoming)
                    emitLastMessage()
                    return
                }
            }
            messages.value.push(incoming)
            scrollToBottom()
            emitLastMessage()
        }
        echoChannel = window.Echo.channel('community-chat.' + props.channel)
        echoChannel.listen('.ChatMessageCreated', echoListener)
    })
}

const unsubscribe = () => {
    if (echoChannel && echoListener) {
        echoChannel.stopListening('.ChatMessageCreated', echoListener)
        echoChannel = null
        echoListener = null
    }
}

onMounted(() => {
    fetchMessages()
    subscribe()
})

onUnmounted(unsubscribe)

watch(() => props.channel, () => {
    unsubscribe()
    messages.value = []
    fetchMessages()
    subscribe()
})
</script>

<template>
    <div :class="fullscreen ? 'flex flex-col gap-2 h-full' : 'flex flex-col gap-2'">
        <div ref="listEl" :class="fullscreen ? 'flex-1 min-h-0' : 'h-80'" class="flex flex-col gap-1.5 overflow-y-auto px-1" @scroll="onScroll">
            <div v-if="isLoading" class="flex items-center justify-center h-full">
                <Spinner/>
            </div>
            <template v-else>
                <MessageBubble v-for="message in messages" :key="message.id" :message="message" @delete="remove"/>
                <div v-if="!messages.length" class="text-center text-sm text-slate-500 dark:text-slate-400 py-6">
                    {{ $t('No messages yet - be the first to write something') }}
                </div>
            </template>
        </div>
        <Input v-model="body"
               :placeholder="page.props.auth.user ? $t('Enter message...') : $t('Sign in to write a message')"
               :disabled="!page.props.auth.user"
               font-size="base"
               text-area
               @push="send"
               @onFocus="goToLoginIfGuest"
        >
            <template #left-icon>
                <div v-if="page.props.auth.user" class="flex items-center gap-1.5 shrink-0">
                    <EmojiPickerButton @pick="insertEmoji"/>
                    <GifPickerButton @pick="sendGif"/>
                </div>
            </template>
            <template #right-icon>
                <div class="flex items-center gap-2 shrink-0">
                    <label v-if="page.props.can_post_as_chat_admin"
                           class="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 cursor-pointer select-none">
                        <input v-model="asAdmin" type="checkbox" class="rounded border-slate-300 dark:border-slate-600"/>
                        {{ $t('Admin') }}
                    </label>
                    <button @click="send">
                        <MaskIcon src="/assets/icons/send.svg" :class="{'!bg-blue-600 dark:!bg-cyan-300': body.length > 0}" class="w-6 h-6 bg-slate-300 dark:bg-slate-700"/>
                    </button>
                </div>
            </template>
        </Input>
    </div>
</template>
